import {lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction, type ReactNode} from 'react';
import {Empty, ErrorNotice, Loading, ResourceView, shortId} from './components';
import {getTeamRole, type TeamRoleId} from '../src/team';
import {PRODUCT_ENTRY_LABEL} from '../src/product';
import {AgentCostReadout, NativeTeamTaskList, TeamUsageSummary, nativeExecutionStatus, nativeInputRequests, nativeTeamProjection, type NativeTeamProjection} from './team-workspace';
import {WorkspaceShell, WorkspaceSidebar, type WorkspacePage} from './workspace-ui';
import {useHermes, currentSession, errorDetails, type NativeChatTarget} from './lib/store';
import {nativeMessageAccepted} from './lib/native-message-delivery';
import {workspaceContextQuery} from './lib/workspace-location';
import {NativeQuestion} from './native-question';
import {NativeConnection} from './native-connection';
import type {ErrorDetails, HermesState} from './lib/types';
import {APP_BASE, Link, navigate, usePath, useSearch, currentLocation} from './router';
import {accountHref} from '../src/account-routes';
import {OrgChart} from './org-chart';
import {messageDisplayPrefix, userMessageDisplayText} from './lib/conversation-messages';
import {BotSessionTokens,BotSessionCostError} from './workspace-usage';
import {WhatsAppDialog} from './whatsapp-dialog';
import {ChatActivityChips} from './chat-activity';
import {NativeWorkStatus} from './native-work-status';
import {ChatComposer, type ComposerAttachment, type ComposerDraftReset, type ComposerEarlierDraft} from './chat-composer';
import {NativeTranscript} from './native-transcript';
import {CHAT_NAVIGATION_EVENT} from './chat-navigation';
import {useChatFollow} from './use-chat-follow';
import {mergeNativeMessages} from './lib/hermes/history-pages';
import type {ChatAttachment} from '../src/chat-attachments';
import {readChatDrafts, saveChatDrafts, type PendingChatInput} from './platform/drafts';
import {platform, reportPlatformError} from './platform/transport';
import {UNSAVED_WORK_EVENT, type UnsavedWorkQuery} from './platform/unsaved-work';

const NativeTerminal = lazy(() => import('./native-terminal').then(module => ({default: module.NativeTerminal})));

type Actions = ReturnType<typeof useHermes>['actions'];
type Props = {state: HermesState; actions: Actions};
type ConversationView = 'conversation' | 'terminal';

function retainChatInUrl(sessionId: string): void {
  const location = currentLocation(), params = new URLSearchParams(location.search);
  if (params.has('chat')) return;
  params.set('chat', sessionId);
  navigate(`${location.pathname}?${params}`, true);
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function MessageDisplayStatus({state}: {state: HermesState}) {
  if (state.nativeConnection.status === 'error') return null;
  if (state.nativeCommunication.error) return <ErrorNotice error={state.nativeCommunication.error}/>;
  return <Loading/>;
}


export function RuntimeSettings({state, actions}: Props) {
  const runtime = state.nativeRuntime.data;
  const provisioning=state.accountSettings.data?.provisioning;
  return <section className="native-runtime-settings"><ErrorNotice error={state.nativeRuntime.error}/>
    <Loading active={provisioning?.state==='pending'||provisioning?.state==='running'}/>
    {provisioning?.state==='error' && <><ErrorNotice error={provisioning.error as import('./lib/types').ErrorDetails}/><button className="button" disabled={state.operation.label!==null} onClick={()=>void actions.prepareAccount().catch(()=>undefined)}>Retry team setup</button></>}
    {!runtime && state.nativeRuntime.status === 'loading' && <Loading/>}
    {runtime && <><h2>Your team</h2>
      <p className="native-runtime-status">{runtime.configured
        ? <>{runtime.running ? 'Your team is running.' : 'Your team is stopped.'}</>
        : `Select “${PRODUCT_ENTRY_LABEL}” to give your team a task. Your files and conversations stay in your account.`}</p>
      {!state.accountSettings.data?.modelKey.configured && <p className="field-help">Contact your administrator to configure model access before you select “{PRODUCT_ENTRY_LABEL}”.</p>}
      {!runtime.running && <button type="button" className="button" disabled={state.operation.label !== null || !state.accountSettings.data?.modelKey.configured}
        onClick={() => void actions.configureHermes().catch(() => undefined)}>{PRODUCT_ENTRY_LABEL}</button>}
    </>}
  </section>;
}

function Conversation({state, actions, projection, agentName, view, setView, draft, draftReset, earlierDraft, setDraft, selectionReady, attachments, setAttachments, onSelectAgent,
  submissionId, releaseSubmission}: Props & {
  projection: NativeTeamProjection; agentName: string; view: ConversationView;
  setView: (view: ConversationView) => void;
  draft: string; setDraft: Dispatch<SetStateAction<string>>; selectionReady: boolean;
  draftReset: ComposerDraftReset | undefined;
  earlierDraft: ComposerEarlierDraft | undefined;
  attachments: ComposerAttachment[]; setAttachments: Dispatch<SetStateAction<ComposerAttachment[]>>; onSelectAgent: (id: string | null) => void;
  submissionId: (text: string, attachments: ChatAttachment[]) => {requestId: string; recover: boolean}; releaseSubmission: (requestId: string) => void;
}) {
  const [deliveryError, setDeliveryError] = useState<ErrorDetails | null>(null);
  const openedAt = useRef(Date.now());
  const mounted = useRef(false);
  const conversation = state.nativeConversation.data;
  const detail = state.nativeDetail.data?.session.sessionId === conversation?.session.sessionId ? state.nativeDetail.data : null;
  const selected = state.nativeSelectedAgentId;
  const child = selected !== null && state.nativeAgent.data?.agent.sessionId === selected ? state.nativeAgent.data : null;
  const agent = selected === null ? null : detail?.agents.find(item => item.sessionId === selected);
  const history = useMemo(()=>{
    if(!selectionReady)return undefined;
    if(selected===null)return conversation?.displayHistory;
    if(!child)return undefined;
    return mergeNativeMessages(child.displayHistory,conversation?.displayHistory.filter(message=>
      message.communication?.agentId===selected)??[]);
  },[selectionReady,selected,conversation?.displayHistory,child?.displayHistory]);
  const communication=state.nativeCommunication.data?.sessionId===conversation?.session.sessionId?state.nativeCommunication.data:null;
  const communicationStreams=useMemo(()=>communication?.streams.filter(stream=>selected===null||stream.agent_id===selected)??[],[communication?.streams,selected]);
  const communicationFailures=useMemo(()=>communication?.receipts.filter(receipt=>receipt.error&&(selected===null||receipt.agent_id===selected))??[],[communication?.receipts,selected]);
  const messagePrefix = messageDisplayPrefix(state);
  const connected = selectionReady && state.nativeConnection.status === 'open' && state.nativeTarget?.sessionId === conversation?.session.sessionId;
  const execution = selected === null ? detail?.state : agent;
  const busy = !connected || state.nativeCommunication.status==='error' || state.session.status==='loading';
  const working = connected && execution?.running === true;
  const resuming = connected && execution?.resuming === true && !working;
  const liveTurn = !selectionReady ? null : selected === null ? detail?.liveTurn ?? null : child?.liveTurn ?? null;
  const requests = connected ? detail?.openRequests.filter(request => {
    const owner = record(request.params)?.session_id;
    return typeof owner === 'string' && (selected === null || owner === selected);
  }) ?? [] : [];
  const connections = connected ? detail?.pendingConnections.filter(connection => selected === null || connection.sessionId === selected) ?? [] : [];
  const selectedStatus = agent && detail ? nativeExecutionStatus(agent,
    nativeInputRequests(detail).some(request => request.sessionId === selected) || connections.length > 0) : null;
  const selectedTask = messagePrefix !== null && agent?.assignedTask?.trim()
    ? userMessageDisplayText(agent.assignedTask, messagePrefix) : null;
  const target: NativeChatTarget | null = conversation && state.session.data && selectionReady
    ? {accountId: state.session.data.accountId, sessionId: conversation.session.sessionId, agentId: selected} : null;
  const result = state.nativeMessageResult.data;
  const admission = result?.receipt?.sessionId === target?.sessionId && result?.receipt?.agentId === selected ? result.receipt : undefined;
  const replyPending = (status: string | undefined) => status === 'queued' || status === 'running';
  const pendingReply = communicationStreams.some(stream => !stream.error)
    || communication?.receipts.some(receipt => !receipt.message_removed && (selected === null || receipt.agent_id === selected) && replyPending(receipt.status))
    || admission?.communication && !communication?.receipts.some(receipt => receipt.request_id === admission.requestId) && replyPending(admission.communication.state);
  const replyStatus = connected && state.nativeCommunication.status !== 'error'
    ? state.nativeMessageResult.status === 'loading' ? 'sending' : pendingReply ? 'preparing' : null : null;
  const communicationError = result?.receipt?.sessionId === target?.sessionId && result?.receipt?.agentId === selected
    ? result.receipt.communication?.error : undefined;
  const activity = selectionReady ? selected === null ? detail?.chatActivity : child?.chatActivity : undefined;
  const latestTurn = activity?.turns.at(-1) ?? null;
  const previousFailure = !working && latestTurn?.status === 'failed' && latestTurn.finishedAt !== null
    && Date.parse(latestTurn.finishedAt) < openedAt.current;
  const failureNotices = <><ErrorNotice error={liveTurn?.error ? {name: 'AgentError', message: liveTurn.error, cause: liveTurn.error_surface} : null}/>
    {execution?.error && execution.error !== liveTurn?.error && <ErrorNotice error={{name: 'AgentError', message: execution.error}}/>}
    <BotSessionCostError state={state} sessionId={selected}/></>;
  const hasTranscript = history !== undefined && (history.length > 0 || liveTurn !== null || communicationStreams.length>0);
  const {body, follow, latest, pause, handlers} = useChatFollow(view === 'conversation' && hasTranscript, [history, liveTurn, communicationStreams, requests.length, working, resuming]);
  const historyPage = selected === null ? conversation?.historyPage : child?.historyPage;
  const olderAnchor = useRef<{key: string; offset: number; before: string | null} | null>(null);
  const renderedHistory = useCallback((rendered: readonly Record<string, unknown>[]) => {
    const anchor = olderAnchor.current, container = body.current;
    if (!anchor || !container || rendered !== history || historyPage?.before === anchor.before) return;
    const element = [...container.querySelectorAll<HTMLElement>('[data-message-key]')]
      .find(row => row.dataset.messageKey === anchor.key);
    if (element) container.scrollTop += element.getBoundingClientRect().top - container.getBoundingClientRect().top - anchor.offset;
    olderAnchor.current = null;
  }, [history, historyPage?.before, body]);
  async function loadOlder() {
    const container = body.current;
    pause();
    if (container) {
      const top = container.getBoundingClientRect().top;
      const element = [...container.querySelectorAll<HTMLElement>('[data-message-key]')]
        .find(row => row.getBoundingClientRect().bottom > top);
      if (element?.dataset.messageKey) olderAnchor.current = {key: element.dataset.messageKey,
        offset: element.getBoundingClientRect().top - top, before: historyPage?.before ?? null};
    }
    try {await actions.loadOlderNativeMessages();} catch {olderAnchor.current = null;}
  }
  useEffect(() => {
    mounted.current = true;
    return () => {mounted.current = false;};
  }, []);
  async function send(text: string, uploaded: ChatAttachment[]): Promise<boolean> {
    if (!target || busy || (!text.trim() && uploaded.length === 0)) return false;
    setDeliveryError(null);
    const attachmentIds = uploaded.map(file => file.id);
    const {requestId, recover} = submissionId(text, uploaded);
    try {
      const sent = await actions.sendNativeMessage(text, attachmentIds, target, requestId, recover);
      const accepted = nativeMessageAccepted(sent);
      if (accepted) setDraft(current => current === text ? '' : current);
      // Both acceptance and an explicit rejection finish this attempt. A lost
      // response keeps its identity so retrying the unchanged draft cannot run it twice.
      releaseSubmission(requestId);
      if (accepted && mounted.current) {
        latest();
        setView('conversation');
      }
      return accepted;
    } catch (error) {
      const details = errorDetails(error);
      if (details.code === 'MESSAGE_NOT_ADMITTED') releaseSubmission(requestId);
      if (mounted.current) setDeliveryError(details);
      return false;
    }
  }
  return <div className={`native-conversation-panel${selected === null && history !== undefined && !hasTranscript && !working ? ' is-empty' : ''}`} hidden={view !== 'conversation'}><div className="conversation-body" ref={body} {...handlers}>
    {selectionReady && agent && <section className="native-agent-overview" aria-label={`${agentName} work`}>
      <div className="native-agent-overview-heading"><h2>{agentName}</h2>
        <p role="status">{connected && state.nativeDetail.status !== 'error' ? selectedStatus?.status
          : state.nativeConnection.status === 'connecting' ? 'Checking work status' : 'Status unavailable'}</p></div>
      {selectedTask && <p className="native-agent-overview-task"><span>Assigned task</span>{selectedTask}</p>}
      {!selectedTask && agent.lifecycleStatus === 'empty' && <p>No task has been assigned.</p>}
    </section>}
    <Loading active={state.nativeHistory.status === 'loading'}/>
    {historyPage?.hasOlder && <button type="button" className="text-button chat-load-older" disabled={!connected || state.nativeHistory.status === 'loading'} onClick={() => void loadOlder()}>Load older messages</button>}
    <ErrorNotice error={state.nativeHistory.error}/>
    {messagePrefix === null ? <MessageDisplayStatus state={state}/>
      : history === undefined ? selected !== null
        ? state.nativeConnection.status !== 'error' && state.nativeAgent.status !== 'error' && <p className="native-agent-history-loading" role="status">Loading {agentName} conversation</p>
        : <Loading active={state.nativeConnection.status !== 'error'}/>
      : conversation && <NativeTranscript accountId={state.session.data?.accountId ?? null} sessionId={conversation.session.sessionId} history={history} agentName={agentName}
          liveTurn={liveTurn} communicationStreams={communicationStreams} communicationFailures={communicationFailures} messagePrefix={messagePrefix} activity={activity} connected={connected}
          acceptedRequestId={result?.receipt?.sessionId === conversation.session.sessionId && result.receipt.agentId === selected ? result.receipt.requestId : undefined}
          nativeSessionId={selected ?? conversation.session.sessionId} onRendered={renderedHistory} agents={detail?.agents??[]} projection={projection} onSelectAgent={onSelectAgent}/>}
    {previousFailure && (liveTurn?.error || execution?.error)
      ? <details className="chat-previous-failure"><summary>Previous request failed · {new Date(latestTurn.finishedAt!).toLocaleString(undefined,{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</summary>{failureNotices}</details>
      : failureNotices}
    {requests.map(request => <NativeQuestion key={String(request.id)} request={request}
      answer={actions.answerNativeRequest} cancel={actions.cancelNativeRequest} busy={busy || state.operation.label !== null}/>)}
    {connections.map(connection => <NativeConnection key={`${connection.sessionId}:${String(connection.operation.op_id)}`}
      connection={connection} answer={actions.answerNativeConnection} busy={busy || state.operation.label !== null}/>)}
    <ErrorNotice error={state.nativeCommunication.error}/>
    {communicationStreams.filter(stream=>stream.error).map(stream=><ErrorNotice key={stream.call_id} error={{name:'CommunicationError',
      message:stream.error!.message,...(stream.error!.stack===undefined?{}:{stack:stream.error!.stack})}}/>)}
    </div>
    {view === 'conversation' && hasTranscript && !follow && <button type="button" className="text-button chat-latest" onClick={() => {
      actions.setNativeWorkDetailsOpen(conversation?.session.sessionId ?? null, false);
      latest();
    }}>Latest messages</button>}
    {view === 'conversation' && <NativeWorkStatus key={`${conversation?.session.sessionId ?? ''}:${selected ?? ''}`} detail={detail}
      sessionId={conversation?.session.sessionId ?? null} selectedSessionId={selected} activity={activity}
      replyStatus={replyStatus}
      messagePrefix={messagePrefix} detailsOpen={state.nativeWorkDetailsOpen} detailsLoading={state.nativeDetail.status === 'loading'} detailsContainer={body.current}
      onDetailsOpenChange={open => {if (open) pause(); actions.setNativeWorkDetailsOpen(conversation?.session.sessionId ?? null, open);}}
      connected={connected && state.nativeDetail.status !== 'error' && (selected === null || state.nativeAgent.status !== 'error')}
      loading={state.nativeConnection.status !== 'error' && state.nativeDetail.status !== 'error' && (selected === null || state.nativeAgent.status !== 'error')
        && (state.nativeConnection.status === 'connecting' || state.nativeDetail.status === 'loading'
          || (state.nativeDetail.status === 'idle' && state.nativeHistory.status === 'loading') || !selectionReady)}
      onSelectAgent={onSelectAgent}/>}
    <ChatActivityChips turn={latestTurn} agents={detail?.agents ?? []} onSelectAgent={onSelectAgent} {...(messagePrefix === null ? {} : {messagePrefix})}/>
    <ChatComposer agentName={agentName} draft={draft} draftReset={draftReset} earlierDraft={earlierDraft} onDraftChange={setDraft} working={working} disabled={busy || view !== 'conversation'}
      attachments={attachments} onAttachmentsChange={setAttachments} uploadFile={async file => {if (!target) throw new Error('Open your conversation before attaching files.'); return actions.uploadChatAttachment(file, target);}}
      onSend={send} onStop={async () => {if (!target) throw new Error('Open your conversation before stopping work.'); await actions.interruptNativeAgent(target);}} errorContent={<>
        <ErrorNotice error={deliveryError}/>
        {communicationError && !communicationFailures.some(receipt=>receipt.request_id===result?.receipt?.requestId) && <ErrorNotice error={{name:'CommunicationError',message:communicationError.message,
          ...(communicationError.stack===undefined?{}:{stack:communicationError.stack})}}/>}
        {result?.status === 'rejected' && <ErrorNotice error={{name: 'MessageDeliveryError', code: 'MESSAGE_NOT_ADMITTED',
          message: 'Your message was not accepted. Your draft is kept.', cause: result}}/>}
      </>}/></div>;

}

export function NativeChat({state, actions, projection, requestedChatId, requestedAgentId, requestedRole, selectionPending, onSelectAgent}: Props & {
  projection: NativeTeamProjection;
  requestedChatId: string | null; requestedAgentId: string | null; requestedRole: string | null;
  selectionPending: boolean; onSelectAgent: (id: string | null) => void;
}) {
  const [view, setView] = useState<ConversationView>('conversation');
  const [whatsappOpen, setWhatsAppOpen] = useState(false);
  const [initialDrafts] = useState(() => readChatDrafts(state.session.data?.accountId));
  const drafts = useRef<Record<string, string>>(initialDrafts.drafts);
  const submissions = useRef<Record<string, PendingChatInput>>(initialDrafts.submissions);
  const [, renderSubmissions] = useState(0);
  const [draftResets, setDraftResets] = useState<Record<string, ComposerDraftReset>>({});
  const [attachmentDrafts, setAttachmentDrafts] = useState<Record<string, ComposerAttachment[]>>(() => Object.fromEntries(
    Object.entries(initialDrafts.submissions).map(([key, input]) => [key, input.attachments.map(uploaded => ({id: uploaded.id, uploaded}))])));
  useEffect(() => {
    const inspect = (event: Event) => {
      if (Object.values(drafts.current).some(Boolean) || Object.keys(submissions.current).length || Object.values(attachmentDrafts).some(files => files.length)) {
        (event as UnsavedWorkQuery).detail.blocked = true;
      }
    };
    window.addEventListener(UNSAVED_WORK_EVENT, inspect);
    return () => window.removeEventListener(UNSAVED_WORK_EVENT, inspect);
  }, [attachmentDrafts]);
  useEffect(() => {
    function navigateChat(event: Event) {
      const action = (event as CustomEvent<string>).detail;
      if (action === 'conversation' || action === 'terminal') setView(action);
      else if (action === 'whatsapp') setWhatsAppOpen(true);
    }
    window.addEventListener(CHAT_NAVIGATION_EVENT, navigateChat);
    return () => window.removeEventListener(CHAT_NAVIGATION_EVENT, navigateChat);
  }, []);
  const defaultChatId = useRef<string | null>(null);
  const selected = state.nativeSelectedAgentId;
  const detail = state.nativeDetail.data;
  const conversation=state.nativeConversation.data;
  // The server resolves the default chat after the composer is already usable.
  // Keep that draft when its real ID is inserted into the URL. Other chats and
  // agents have separate, document-local drafts; none are sent during reconnect.
  if (requestedChatId === null && conversation) defaultChatId.current = conversation.session.sessionId;
  const draftChat = platform().kind === 'desktop' ? requestedChatId ?? conversation?.session.sessionId ?? 'unselected'
    : requestedChatId === null || requestedChatId === defaultChatId.current ? 'default' : requestedChatId;
  const draftKey = JSON.stringify([draftChat, requestedRole && requestedRole !== 'head' ? requestedRole : requestedAgentId]);
  const selectionReady = !selectionPending && requestedAgentId === selected && conversation !== null &&
    (requestedChatId === null || requestedChatId === conversation.session.sessionId);
  const accountId = state.session.data?.accountId;
  const persistDrafts = () => {
    // A late upload or send callback must not restore drafts cleared by sign-out.
    if (accountId && currentSession()?.accountId === accountId) saveChatDrafts(accountId, {drafts: drafts.current, submissions: submissions.current});
  };
  const pendingKey = JSON.stringify(['unselected', requestedRole && requestedRole !== 'head' ? requestedRole : requestedAgentId]);
  const hasPendingDraft = platform().kind === 'desktop' && draftChat !== 'unselected' &&
    Boolean(drafts.current[pendingKey] || submissions.current[pendingKey] || attachmentDrafts[pendingKey]?.length);
  const hasCurrentDraft = Boolean(drafts.current[draftKey] || submissions.current[draftKey] || attachmentDrafts[draftKey]?.length);
  function movePendingDraft(append: boolean) {
    if (append && (submissions.current[draftKey] || submissions.current[pendingKey])) {
      reportPlatformError(new Error('Complete the pending send before you combine these drafts.'));
      return;
    }
    const previous = drafts.current[draftKey] ?? '', pending = drafts.current[pendingKey] ?? '';
    const value = previous && pending ? `${previous}\n\n${pending}` : previous || pending;
    drafts.current[draftKey] = value;
    delete drafts.current[pendingKey];
    if (submissions.current[pendingKey]) submissions.current[draftKey] = submissions.current[pendingKey];
    delete submissions.current[pendingKey];
    setAttachmentDrafts(before => {
      const next = {...before, [draftKey]: [...(before[draftKey] ?? []), ...(before[pendingKey] ?? [])]};
      delete next[pendingKey];
      return next;
    });
    setDraftResets(resets => ({...resets, [draftKey]: {previous, value}}));
    try {persistDrafts();} catch (error) {reportPlatformError(error);}
  }
  useEffect(() => {
    // A resolved chat can already have a saved draft. Keep both until the user combines them.
    if (hasPendingDraft && !hasCurrentDraft) movePendingDraft(false);
  });
  const agent = selected === null ? null : detail?.agents.find(item => item.sessionId === selected);
  const roleId = selected === null ? 'head' : agent?.roleId;
  const role = roleId ? getTeamRole(roleId) : null;
  const messagePrefix = messageDisplayPrefix(state);
  const agentName = role?.name ?? (messagePrefix !== null && agent?.title ? userMessageDisplayText(agent.title, messagePrefix, {folded: true}) : undefined)
    ?? (selected === null ? getTeamRole('head').name : shortId(selected));
  return <section className={`agent-chat${view === 'terminal' ? ' view-terminal' : ''}`} aria-label={`${agentName} conversation`}><div className="chat-heading">
    <div className="view-switch" aria-label="Conversation view"><button type="button" aria-pressed={view === 'conversation'} onClick={() => setView('conversation')}>Chat</button>
      <button type="button" aria-pressed={view === 'terminal'} onClick={() => setView('terminal')}>Terminal</button>
      <button type="button" className="whatsapp-launch" aria-haspopup="dialog" onClick={() => setWhatsAppOpen(true)}>
        <img src={`${APP_BASE}/assets/whatsapp-official.svg`} width={23} height={23} alt=""/>WhatsApp</button></div></div>
    {agent && !role && <AgentCostReadout usage={state.nativeConnection.status === 'open' && state.nativeDetail.status !== 'error' ? agent.usage : null}/>}
    {selected !== null && <ErrorNotice error={state.nativeAgent.error}/>}
    <Conversation key={draftKey} state={state} actions={actions} projection={projection} agentName={agentName} view={view} setView={setView}
      earlierDraft={hasPendingDraft && hasCurrentDraft ? {text: drafts.current[pendingKey] ?? '', attachments: attachmentDrafts[pendingKey] ?? [],
        pendingSubmission: Boolean(submissions.current[draftKey] || submissions.current[pendingKey]), append: () => movePendingDraft(true)} : undefined}
      draft={drafts.current[draftKey] ?? ''} draftReset={draftResets[draftKey]} setDraft={update => {
        const previous = drafts.current[draftKey] ?? '';
        const value = typeof update === 'function' ? update(previous) : update;
        drafts.current[draftKey] = value;
        if (value !== previous && submissions.current[draftKey]) {
          delete submissions.current[draftKey];
          renderSubmissions(version => version + 1);
        }
        try {persistDrafts();} catch (error) {reportPlatformError(error);}
        // A conditional accepted-send clear must reach a reopened composer.
        // Ordinary edits render the chat tree only when pending-send state changes.
        if (typeof update === 'function' && value !== previous) setDraftResets(resets => ({...resets, [draftKey]: {previous, value}}));
      }}
      submissionId={(text, uploaded) => {
        const attachmentIds = uploaded.map(attachment => attachment.id);
        const previous = submissions.current[draftKey];
        if (previous && previous.text === text && previous.attachmentIds.length === attachmentIds.length &&
            previous.attachmentIds.every((id, index) => id === attachmentIds[index])) return {requestId: previous.requestId, recover: true};
        const requestId = crypto.randomUUID();
        submissions.current[draftKey] = {requestId, text, attachmentIds, attachments: uploaded};
        persistDrafts();
        return {requestId, recover: false};
      }}
      releaseSubmission={requestId => {
        if (submissions.current[draftKey]?.requestId === requestId) {
          delete submissions.current[draftKey];
          renderSubmissions(version => version + 1);
          try {persistDrafts();} catch (error) {reportPlatformError(error);}
        }
      }}
      selectionReady={selectionReady} onSelectAgent={onSelectAgent} attachments={attachmentDrafts[draftKey] ?? []}
      setAttachments={update => setAttachmentDrafts(previous => {
        const before = previous[draftKey] ?? [];
        const next = typeof update === 'function' ? update(before) : update;
        if (before.length !== next.length || before.some((item, index) => item.id !== next[index]?.id)) delete submissions.current[draftKey];
        try {persistDrafts();} catch (error) {reportPlatformError(error);}
        return {...previous, [draftKey]: next};
      })}/>
    {view === 'terminal' && (!accountId || !detail || !selectionReady ? <Loading active={state.nativeConnection.status !== 'error'}/>
        : <Suspense fallback={<Loading/>}><NativeTerminal key={JSON.stringify([accountId, detail.session.sessionId, selected])} accountId={accountId}
          sessionId={detail.session.sessionId} agentId={selected} openTerminal={actions.openNativeTerminal}/></Suspense>)}
    {whatsappOpen && <WhatsAppDialog key={JSON.stringify([accountId, detail?.session.sessionId])} state={state} actions={actions}
      sessionId={detail?.session.sessionId} onClose={() => setWhatsAppOpen(false)}/>}
  </section>;
}

function WorkChatSidebar({state, actions, selectedChatId, onSelect, onCreate, onNavigate}: Props & {
  selectedChatId: string | null; onSelect: (id: string) => void; onCreate: (title?: string) => Promise<boolean>;
  onNavigate: () => void;
}) {
  const busy = state.operation.label !== null;
  const enabled = state.nativeRuntime.data?.configured === true && state.accountSettings.data?.modelKey.configured === true;
  const messagePrefix = messageDisplayPrefix(state);
  const rows = state.nativeSessions.status === 'error' || messagePrefix === null ? [] : (state.nativeSessions.data ?? []).map(item => ({
    id: item.sessionId, title: userMessageDisplayText(item.title?.trim() ?? '', messagePrefix, {folded: true}) || 'Untitled chat',
    preview: item.preview === null ? null : userMessageDisplayText(item.preview, messagePrefix, {folded: true}), updatedAt: item.updatedAt
  })).sort((a, b) => (b.updatedAt === null ? 0 : new Date(b.updatedAt).getTime()) - (a.updatedAt === null ? 0 : new Date(a.updatedAt).getTime()));
  return <WorkspaceSidebar chats={rows} selectedChatId={selectedChatId} onSelect={onSelect} onCreate={() => onCreate()}
    busy={busy} createDisabled={!enabled} loading={state.nativeSessions.status === 'loading'} onNavigate={onNavigate}
    status={<><ErrorNotice error={state.nativeSessions.error}/>{messagePrefix === null && <MessageDisplayStatus state={state}/>}</>}/>;
}

export function NativeWorkspace({state, actions, view = 'workspace', children}: Props & {view?: WorkspacePage; children?: ReactNode}) {
  const path = usePath();
  const search = useSearch();
  const parameters = new URLSearchParams(search);
  const requestedChatId = parameters.get('chat');
  const requestedRole = parameters.get('role');
  if (requestedRole) getTeamRole(requestedRole);
  const settingsUrl = `${APP_BASE}/settings${workspaceContextQuery(search)}`;
  const accountId = state.session.data?.accountId;
  const sessionToken = state.session.data?.csrfToken;
  const configured = state.nativeRuntime.data?.configured === true;
  const keyConfigured = state.accountSettings.data?.modelKey.configured === true;
  const opened = state.nativeDetail.data;
  const openedConversation = state.nativeConversation.data;
  const openedRequestedChat = openedConversation !== null && (requestedChatId === null || openedConversation.session.sessionId === requestedChatId);
  useEffect(() => {
    // The work metadata owns the title. Do not replace an existing readable URL
    // with an untitled slug while the lightweight admission read is ready first.
    if (!openedRequestedChat || !opened || !accountId) return;
    const params = new URLSearchParams(search);
    params.set('chat', opened.session.sessionId);
    const href = accountHref(`${path}?${params}`, APP_BASE, opened.session.title ?? 'Work chat');
    if (`${window.location.pathname}${window.location.search}` !== href) navigate(href, true);
  }, [openedRequestedChat, opened?.session.sessionId, opened?.session.title, accountId, path, search]);
  const selectedFromUrl = parameters.has('agent') ? parameters.get('agent')
    : requestedRole && requestedRole !== 'head' ? opened?.agents.find(agent => agent.roleId === getTeamRole(requestedRole).id)?.sessionId ?? null : null;
  const unresolvedRole = !parameters.has('agent') && requestedRole !== null && requestedRole !== 'head' && selectedFromUrl === null;
  const chat = useRef<HTMLDivElement>(null);
  const pendingReview = useRef<string | null>(null);
  const [reviewVersion, setReviewVersion] = useState(0);
  const [reviewError, setReviewError] = useState<ErrorDetails | null>(null);
  const [chatRecovery, setChatRecovery] = useState<{accountId: string; error: ErrorDetails} | null>(null);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {mounted.current = false;};
  }, []);
  // The URL owns the requested work chat. The store owns each active request and
  // server response; changing the URL invalidates the preceding poll generation.
  useEffect(() => {
    if (!accountId) return;
    if (state.nativeConnection.status === 'connecting' && state.nativeTarget?.sessionId === requestedChatId) return;
    let current = true;
    void actions.openHermes(requestedChatId).then(mapping => {
      if (!current || !mapping || requestedChatId !== null) return;
      retainChatInUrl(mapping.sessionId);
    }).catch(async error => {
      const details = errorDetails(error);
      if (!current || requestedChatId === null || details.status !== 404 || details.code !== 'WORK_CHAT_UNAVAILABLE') return;
      // A replaced browser cookie can belong to a different signed-in account.
      // Confirm identity before replacing a rejected URL selection. Other errors
      // retain their own recovery; never reinterpret a runtime failure as a bad link.
      try {
        const principal = await actions.revalidateSession();
        if (!current || principal?.accountId !== accountId) return;
        const url = new URL(window.location.href);
        const location = currentLocation();
        url.pathname = location.pathname; url.search = location.search;
        if (url.searchParams.get('chat') !== requestedChatId) return;
        setChatRecovery({accountId, error: details});
        for (const key of ['chat', 'agent', 'role']) url.searchParams.delete(key);
        actions.closeHermes();
        navigate(`${url.pathname}${url.search}${url.hash}`, true);
      } catch { /* Keep the ownership diagnostic when identity cannot be confirmed. */ }
    });
    return () => {current = false;};
  }, [accountId, sessionToken, requestedChatId, actions]);
  useEffect(() => {
    if (!openedRequestedChat || unresolvedRole || state.nativeSelectedAgentId === selectedFromUrl) return;
    void actions.selectNativeAgent(selectedFromUrl).catch(() => undefined);
  }, [openedRequestedChat, openedConversation?.session.sessionId, selectedFromUrl, unresolvedRole, state.nativeSelectedAgentId, actions]);
  useEffect(() => {
    const requested = pendingReview.current;
    if (!requested || !opened) return;
    const currentSession = state.nativeSelectedAgentId ?? opened.session.sessionId;
    if (currentSession !== requested) return;
    const input = chat.current?.querySelector<HTMLElement>('.native-question input, .native-question textarea, .native-question button');
    if (!input) return;
    input.focus({preventScroll: true});
    pendingReview.current = null;
  }, [reviewVersion, state.nativeSelectedAgentId, state.nativeAgent.status, opened]);
  async function refreshWorkspace(): Promise<void> {
    const mapping = await actions.refreshHermes(requestedChatId);
    if (mounted.current && mapping && requestedChatId === null) retainChatInUrl(mapping.sessionId);
  }
  function selectChat(id: string): void {
    if (id !== opened?.session.sessionId) actions.closeHermes();
    const params = new URLSearchParams(search);
    params.set('chat', id); params.delete('agent'); params.delete('role');
    navigate(`${APP_BASE}/workspace?${params}`);
  }
  async function createChat(title?: string): Promise<boolean> {
    try {
      const created = await actions.createNativeChat(title);
      if (!mounted.current) return false;
      selectChat(created.sessionId);
      return true;
    } catch {return false;}
  }
  useEffect(() => {
    function navigateChat(event: Event) {
      if ((event as CustomEvent<string>).detail === 'new-chat' && view === 'workspace' && state.operation.label === null && configured && keyConfigured) void createChat();
    }
    window.addEventListener(CHAT_NAVIGATION_EVENT, navigateChat);
    return () => window.removeEventListener(CHAT_NAVIGATION_EVENT, navigateChat);
  });
  const openedSessionId = openedConversation?.session.sessionId;
  const selectAgent = useCallback((id: string | null): void => {
    if (!openedRequestedChat || !openedSessionId) return;
    const params = new URLSearchParams(search);
    params.set('chat', openedSessionId); params.delete('role');
    if (id === null || id === openedSessionId) params.delete('agent'); else params.set('agent', id);
    navigate(`${view === 'team' ? `${APP_BASE}/workspace` : path}?${params}`);
  }, [openedRequestedChat, openedSessionId, search, view, path]);
  function selectRole(roleId: TeamRoleId): void {
    if (roleId === 'head') {selectAgent(null); return;}
    const agent = opened?.agents.find(item => item.roleId === roleId);
    if (!agent) throw new Error(`The ${getTeamRole(roleId).name} conversation is missing from your team.`);
    selectAgent(agent.sessionId);
  }
  const projection = useMemo(() => nativeTeamProjection(state), [state.nativeConnection.status, state.nativeConnection.error,
    state.nativeDetail, state.nativeTarget?.sessionId, state.accountSettings]);
  const messagePrefix = messageDisplayPrefix(state);
  const workspaceErrors = [...new Set(state.nativeConnection.error ? [state.nativeConnection.error]
    : [state.accountSettings.error, state.nativeRuntime.error, state.nativeDetail.error, state.nativeEvents.error, projection.error]
      .filter((error): error is ErrorDetails => error !== null))];
  if (unresolvedRole && openedRequestedChat && state.nativeDetail.status === 'ready') {
    workspaceErrors.push(errorDetails(new Error(`The ${getTeamRole(requestedRole!).name} conversation is missing from your team.`)));
  }
  const unavailableChat = requestedChatId !== null && state.nativeConnection.error?.status === 404
    && state.nativeConnection.error.code === 'WORK_CHAT_UNAVAILABLE';
  function reviewRequest(sessionId: string): void {
    const isHead = sessionId === opened?.session.sessionId || sessionId === opened?.state.liveSessionId;
    if (!isHead && !opened?.agents.some(agent => agent.sessionId === sessionId)) {
      setReviewError(errorDetails(new Error(`The request belongs to native session ${sessionId}, which is not present in the selected chat.`)));
      return;
    }
    setReviewError(null);
    pendingReview.current = isHead ? opened!.session.sessionId : sessionId;
    setReviewVersion(value => value + 1);
    selectAgent(isHead ? null : sessionId);
  }
  const blockedChat = state.nativeConnection.status === 'error' && !openedRequestedChat;
  const conversation = !children && view !== 'team' && accountId && !blockedChat ? <aside className="workspace-conversation" ref={chat} aria-label="Selected agent conversation">
    <ErrorNotice error={reviewError}/>
    <NativeChat key={accountId} state={state} actions={actions} projection={projection} requestedChatId={requestedChatId} requestedAgentId={selectedFromUrl}
      requestedRole={parameters.has('agent') ? null : requestedRole} selectionPending={unresolvedRole} onSelectAgent={selectAgent}/>
  </aside> : undefined;
  return <WorkspaceShell sidebar={controls => <WorkChatSidebar state={state} actions={actions}
    selectedChatId={requestedChatId ?? opened?.session.sessionId ?? null} onSelect={selectChat} onCreate={createChat} {...controls}/>}
    conversation={conversation} conversationInMain={view === 'workspace'} className={`native-workspace${view === 'team' ? ' is-chart-page' : view === 'workspace' ? ' is-chat-page' : ''}`}>
    {chatRecovery && chatRecovery.accountId === accountId && <div className="workspace-chat-recovery" role="status">
      <p>The previous chat is unavailable in this account. {openedRequestedChat && state.nativeConnection.status === 'open'
        ? 'Your work chat is open.' : state.nativeConnection.status === 'error'
          ? 'Your work chat could not be opened. See the connection error below.' : 'Opening your work chat…'}</p>
      <details><summary>Previous chat details</summary><ErrorNotice error={chatRecovery.error}/></details>
      <button type="button" className="text-button" onClick={() => setChatRecovery(null)}>Dismiss</button>
    </div>}
    {!children && (workspaceErrors.length > 0 || (configured && keyConfigured && state.nativeConnection.status === 'closed')) && <>
      <div className="workspace-connection-status">
        {unavailableChat ? <div role="alert"><p>This chat is unavailable in your signed-in account. Choose one of your work chats, or open your account's default chat.</p>
          <button type="button" className="button" disabled={state.operation.label !== null}
            onClick={() => {actions.closeHermes(); navigate(`${APP_BASE}/workspace`, true);}}>Open my work chat</button></div>
          : <button type="button" className="text-button" disabled={state.operation.label !== null || state.nativeConnection.status === 'connecting'}
            onClick={() => void refreshWorkspace().catch(() => undefined)}>Reconnect</button>}</div>
      {workspaceErrors.map((error, index) => <ErrorNotice key={index} error={error}/>)}</>}
    {children ? <div className="workspace-secondary-content">{children}</div>
      : workspaceErrors.length > 0 ? null
      : !state.accountSettings.data ? <Loading/>
      : !keyConfigured ? <div className="team-workspace-setup"><Empty title="Set up your team."
        action={<Link className="button" href={settingsUrl}>Open Settings</Link>}>Contact your administrator to configure your team's model connection.</Empty></div>
      : !configured ? <div className="team-workspace-setup"><RuntimeSettings state={state} actions={actions}/></div>
      : <ResourceView resource={state.nativeDetail}>{detail => <>
          {view === 'team' ? <section className="workspace-chart-page" aria-label="Your team">
            <OrgChart projection={projection} selectedSessionId={state.nativeSelectedAgentId} onSelect={selectAgent}
              renderUsage={member => <BotSessionTokens state={state} member={member}/>}/></section>
          : view === 'tasks' ? <div className="workspace-tasks-page"><section className="workspace-task-panel" aria-label="Work in this chat"><NativeTeamTaskList state={state} projection={projection} onSelect={selectRole} onSelectSession={selectAgent} onReview={reviewRequest}/></section>
          {messagePrefix !== null && detail.agents.some(agent => agent.roleId === null) && <details className="workspace-support-details"><summary>Child agents and saved work</summary>
            <div className="native-session-list">{detail.agents.filter(agent => agent.roleId === null).map(agent =>
              <div className="team-extra-session" key={agent.sessionId}><button type="button" className={`session-row ${state.nativeSelectedAgentId === agent.sessionId ? 'selected' : ''}`} onClick={() => selectAgent(agent.sessionId)}>
                <span><strong>{agent.title === null ? shortId(agent.sessionId) : userMessageDisplayText(agent.title, messagePrefix, {folded: true})}</strong>
                  <small>{userMessageDisplayText(agent.goal, messagePrefix)}</small>
                  <small>{agent.executionKind === 'scheduled' ? 'Scheduled agent' : 'Child agent'}{agent.currentTool && ` · ${agent.currentTool}`}</small></span>
                <span className={`session-status ${agent.running ? 'status-running' : ''}`}>{agent.status}</span></button>
                <AgentCostReadout usage={state.nativeConnection.status === 'open' ? agent.usage : null}/></div>)}</div></details>}
          <details className="workspace-support-details"><summary>Usage in this chat</summary><TeamUsageSummary state={state} projection={projection}/></details>
          {state.nativeEvents.data && state.nativeEvents.data.events.length > 0 && <details className="workspace-support-details"><summary>Activity details</summary>
            <pre>{JSON.stringify(state.nativeEvents.data.events, null, 2)}</pre></details>}
          </div> : null}
        </>}</ResourceView>}
  </WorkspaceShell>;
}
